import React, { useState, useEffect, useMemo } from "react";
import { Box, Stepper, Step, StepLabel, Typography, Stack, Button, Grid, TextField, Autocomplete, Chip, MenuItem, Paper, Alert } from '@mui/material';
import { CheckCircle, AssignmentOutlined, PersonAddOutlined, RateReviewOutlined, Layers, ListAltOutlined, Schedule } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getData, postData } from "../../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/endpoints";

function CreateSubTask({ taskData, onClose, onSuccess }) {
    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [projectData, setProjectData] = useState(null);
    const [loading, setLoading] = useState(false);
    const steps = ['Sub-Task Details', 'Assignment', 'Review'];
    const [apiErrorMessage, setApiErrorMessage] = useState("");

    const [formData, setFormData] = useState({
        title: "",
        taskType: "single",
        workType: "",
        description: "",
        startDate: dayjs(),
        endDate: dayjs(),
        estHours: "01",
        estMinutes: "00",
        priority: "Medium",
        department: null,
        team: null,
        assignee: [],
    });

    // Calculate maximum allowed hours based on selected date range (8 hours per day)
    const maxAllowedHoursByDate = useMemo(() => {
        if (!formData.startDate || !formData.endDate) return 8;
        const days = formData.endDate.diff(formData.startDate, 'day') + 1;
        return days * 8;
    }, [formData.startDate, formData.endDate]);

    const updateField = (name, value) => {
        setFormData(prev => {
            const updated = { ...prev, [name]: value };

            // Logic: If user selects the maximum hours for the range, reset minutes to 0
            if (name === 'estHours' && parseInt(value, 10) === maxAllowedHoursByDate) {
                updated.estMinutes = "00";
            }
            return updated;
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        updateField(name, value);
    };

    const validateForm = () => {
        let newErrors = {};

        if (activeStep === 0) {
            if (!formData.title.trim()) newErrors.title = "Sub-task title is required";

            if (!formData.workType) {
                newErrors.workType = "Work type is required";
            }
        }

        if (activeStep === 1) {
            const parentStart = dayjs(taskData?.start_date);
            const parentEnd = dayjs(taskData?.due_date);

            if (formData.startDate && (formData.startDate.isBefore(parentStart, 'day') || formData.startDate.isAfter(parentEnd, 'day'))) {
                newErrors.startDate = "Date outside parent task range";
            }
            if (formData.endDate && formData.endDate.isAfter(parentEnd, 'day')) {
                newErrors.endDate = "Cannot exceed parent deadline";
            }

            if (!formData.department) newErrors.department = "Department is required";
            if (!formData.team) newErrors.team = "Team is required";
            if (!formData.startDate) newErrors.startDate = "Start date is required";
            if (!formData.endDate) newErrors.endDate = "End date is required";

            const totalSelectedHours = parseInt(formData.estHours, 10) + (parseInt(formData.estMinutes, 10) / 60);
            if (totalSelectedHours <= 0) {
                newErrors.estHours = "Estimated time must be greater than 0";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNextStep = async () => {
        if (validateForm()) {
            if (activeStep < steps.length - 1) {
                setActiveStep((prev) => prev + 1);
            } else {
                await handleSubmit();
            }
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setApiErrorMessage("");
        try {
            const payload = {
                parent_task_id: taskData.id, // Ensure this is sent to the backend
                task_title: formData.title,
                work_type: formData.workType,
                description: formData.description,
                priority: formData.priority,
                start_date: formData.startDate ? formData.startDate.format('YYYY-MM-DD') : null,
                due_date: formData.endDate ? formData.endDate.format('YYYY-MM-DD') : null,
                estimated_hours: parseInt(formData.estHours, 10) + (parseInt(formData.estMinutes, 10) / 60),
                assignees: formData.assignee.map(member => member.id),
            };

            // Remove the parentheses () from createSubTask
            const response = await postData(API_ENDPOINTS.createSubTask, payload);

            if (response.status === "SUCCESS") {
                if (onSuccess) onSuccess();
                if (onClose) onClose();
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message;
            setApiErrorMessage(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (taskData) {
            getData(API_ENDPOINTS.fetchProjectById(taskData?.project_id))
                .then(res => setProjectData(res.message.data));
        }
    }, [taskData]);

    const departmentOptions = useMemo(() => projectData?.departments || [], [projectData]);
    const getTeamsForDept = (deptObj) => deptObj?.teams || [];
    const getMembersForTeam = (teamObj) =>
        teamObj?.members?.map(m => ({ ...m, label: `${m.first_name} ${m.last_name}` })) || [];

    const ReviewItem = ({ label, value, icon }) => (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#64748b', mb: 0.5 }}>
                {icon}
                <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{label}</Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500, ml: 3 }}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );

    const getStepIcon = (index, active, completed) => {
        if (completed) return <CheckCircle sx={{ color: '#10b981', fontSize: 24 }} />;
        const iconStyle = { color: active ? '#8b5cf6' : '#94a3b8', fontSize: 24 };
        switch (index) {
            case 0: return <AssignmentOutlined sx={iconStyle} />;
            case 1: return <PersonAddOutlined sx={iconStyle} />;
            case 2: return <RateReviewOutlined sx={iconStyle} />;
            default: return <AssignmentOutlined sx={iconStyle} />;
        }
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Sub-Task Title *"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                error={!!errors.title}
                                helperText={errors.title}
                                size="small"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Work Type *"
                                name="workType"
                                value={formData.workType}
                                onChange={handleChange}
                                // Add these two lines
                                error={!!errors.workType}
                                helperText={errors.workType}
                            >
                                <MenuItem value="Bug">Bug</MenuItem>
                                <MenuItem value="Enhancement">Enhancement</MenuItem>
                                <MenuItem value="Feature">Feature</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                placeholder="Describe the work..."
                            />
                        </Grid>
                    </Grid>
                );

            case 1:
                const parentStart = dayjs(taskData?.start_date);
                const parentEnd = dayjs(taskData?.due_date);
                const today = dayjs();

                // The sub-task start cannot be before today OR the parent start date
                const absoluteMinStart = today.isAfter(parentStart) ? today : parentStart;
                // If the parent task's due date has already passed relative to the earliest
                // allowed start, capping maxDate to it would invert the range and disable
                // every day — treat the cap as unset in that case instead.
                const safeMaxDate = parentEnd.isValid() && parentEnd.isAfter(absoluteMinStart) ? parentEnd : null;
                return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Autocomplete
                                    options={departmentOptions}
                                    getOptionLabel={(opt) => opt.department_name || ""}
                                    value={formData.department}
                                    onChange={(_, val) => {
                                        updateField('department', val);
                                        updateField('team', null);
                                        updateField('assignee', []);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Department *" size="small" error={!!errors.department} helperText={errors.department} />}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Autocomplete
                                    disabled={!formData.department}
                                    options={getTeamsForDept(formData.department)}
                                    getOptionLabel={(opt) => opt.team_name || ""}
                                    value={formData.team}
                                    onChange={(_, val) => {
                                        updateField('team', val);
                                        updateField('assignee', []);
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Team *" size="small" error={!!errors.team} helperText={errors.team} />}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Autocomplete
                                    multiple
                                    disabled={!formData.team}
                                    options={getMembersForTeam(formData.team)}
                                    getOptionLabel={(opt) => opt.label || ""}
                                    value={formData.assignee}
                                    onChange={(_, val) => updateField('assignee', val)}
                                    renderInput={(params) => <TextField {...params} label="Team Members *" size="small" />}
                                    renderTags={(tagValue, getTagProps) =>
                                        tagValue.map((option, index) => {
                                            const { key, ...tagProps } = getTagProps({ index });
                                            return (
                                                <Chip
                                                    key={key}
                                                    {...tagProps}
                                                    label={option.label}
                                                    size="small"
                                                    sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 500 }}
                                                />
                                            );
                                        })
                                    }
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <DatePicker
                                    label="Start Date *"
                                    value={formData.startDate}
                                    minDate={absoluteMinStart}
                                    maxDate={safeMaxDate}
                                    format='DD/MM/YYYY'
                                    onChange={(newValue) => {
                                        updateField('startDate', newValue);
                                        if (formData.endDate && newValue && newValue.isAfter(formData.endDate)) {
                                            updateField('endDate', newValue);
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!errors.startDate,
                                            helperText: errors.startDate || `Parent Task Start Date: ${parentStart.format('DD/MM/YYYY')}`,
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <DatePicker
                                    label="End Date *"
                                    value={formData.endDate}
                                    minDate={formData.startDate || absoluteMinStart}
                                    maxDate={safeMaxDate}
                                    format='DD/MM/YYYY'
                                    onChange={(newValue) => updateField('endDate', newValue)}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            error: !!errors.endDate,
                                            helperText: errors.endDate || `Parent Task Deadline: ${parentEnd.format('DD/MM/YYYY')}`,
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Hours"
                                        size="small"
                                        value={formData.estHours}
                                        onChange={(e) => updateField('estHours', e.target.value)}
                                        error={!!errors.estHours}
                                        helperText={errors.estHours}
                                    >
                                        {[...Array(maxAllowedHoursByDate + 1)].map((_, i) => (
                                            <MenuItem key={i} value={i < 10 ? `0${i}` : `${i}`}>
                                                {i}h
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Mins"
                                        size="small"
                                        value={formData.estMinutes}
                                        onChange={(e) => updateField('estMinutes', e.target.value)}
                                        disabled={parseInt(formData.estHours, 10) === maxAllowedHoursByDate}
                                    >
                                        {["00", "15", "30", "45"].map(m => (
                                            <MenuItem key={m} value={m}>{m}m</MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField select fullWidth label="Priority" size="small" value={formData.priority} onChange={(e) => updateField('priority', e.target.value)}>
                                    {["Low", "Medium", "High"].map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                );

            case 2:
                return (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Review Sub-Task Details</Typography>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12 }}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', bgcolor: '#f8fafc' }}>
                                    <Typography variant="overline" sx={{ color: '#8b5cf6', fontWeight: 800 }}>Core Information</Typography>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid size={{ xs: 12, md: 6 }}><ReviewItem label="Title" value={formData.title} icon={<AssignmentOutlined fontSize="small" />} /></Grid>
                                        <Grid size={{ xs: 12 }}><ReviewItem label="Description" value={formData.description} icon={<ListAltOutlined fontSize="small" />} /></Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px' }}>
                                    <Typography variant="overline" sx={{ color: '#8b5cf6', fontWeight: 800 }}>Assignment & Schedule</Typography>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid size={{ xs: 12, md: 4 }}><ReviewItem label="Dept / Team" value={`${formData.department?.department_name} / ${formData.team?.team_name}`} icon={<Layers fontSize="small" />} /></Grid>
                                        <Grid size={{ xs: 12, md: 4 }}>
                                            <ReviewItem
                                                label="Timeline"
                                                value={`${formData.startDate?.format('DD MMM YYYY')} to ${formData.endDate?.format('DD MMM YYYY')}`}
                                                icon={<Schedule fontSize="small" />}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 12, md: 4 }}><ReviewItem label="Effort / Priority" value={`${formData.estHours}h ${formData.estMinutes}m | ${formData.priority}`} icon={<Schedule fontSize="small" />} /></Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>ASSIGNEES</Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                {formData.assignee.map((a, i) => <Chip key={i} label={a.label} size="small" sx={{ bgcolor: '#ede9fe' }} />)}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ width: '100%', py: 4, bgcolor: '#f8fafc', borderRadius: '12px 12px 0 0' }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => (
                        <Step key={label} completed={activeStep > index}>
                            <StepLabel StepIconComponent={(props) => getStepIcon(index, props.active, props.completed)}>
                                <Typography sx={{
                                    fontWeight: activeStep === index ? 700 : 500,
                                    fontSize: '0.85rem',
                                    color: activeStep >= index ? '#1e293b' : '#94a3b8'
                                }}>
                                    {label}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Box sx={{ py: 4, px: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ width: '100%', maxWidth: 700 }}>
                    <Box sx={{ minHeight: 250 }}>
                        {getStepContent(activeStep)}
                    </Box>
                    <Box my={3}>
                        {apiErrorMessage && (
                            <Alert
                                severity="error"
                                sx={{ mb: 3, borderRadius: '12px', fontWeight: 600 }}
                            >
                                {apiErrorMessage}
                            </Alert>
                        )}
                    </Box>

                    <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={() => setActiveStep(p => p - 1)}
                            sx={{ color: '#64748b', fontWeight: 600, textTransform: 'none' }}
                        >
                            Back
                        </Button>
                        <Button
                            variant="contained"
                            disabled={loading}
                            onClick={handleNextStep}
                            sx={{
                                bgcolor: '#8b5cf6',
                                '&:hover': { bgcolor: '#7c3aed' },
                                px: 4,
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: '8px'
                            }}
                        >
                            {loading ? 'Creating...' : activeStep === steps.length - 1 ? 'Confirm & Create' : 'Next'}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}

export default CreateSubTask;